/* eslint-disable max-lines-per-function */
import type { CaptionTrack, Level, PlayerController } from "@lumi/player-core";
import { createPlayerController } from "@lumi/player-core";
import React from "react";
import { useTelemetry } from "../../../telemetry/TelemetryContext";

type Sub = { lang: string; url: string; forced?: boolean };

type UserType = {
  id: string;
  email: string;
};

type AuthenticatedClient = {
  postProgress: (p: { assetId: string; positionSec: number; durationSec?: number }) => Promise<{ ok: boolean }>;
  getProgress: (assetId: string) => Promise<{ positionSec: number }>;
  getTitleAssets: (titleId: string) => Promise<{ hlsUrl: string; subtitles?: Array<Sub> }>;
};

/**
 * Compare two caption track lists for equality
 * Extracted to reduce nesting depth in callbacks
 */
function areCaptionListsEqual(
  prevCaptions: ReadonlyArray<CaptionTrack>,
  newCaptions: ReadonlyArray<CaptionTrack>,
): boolean {
  if (prevCaptions.length !== newCaptions.length) {
    return false;
  }
  for (let i = 0; i < prevCaptions.length; i++) {
    const prev = prevCaptions[i];
    const next = newCaptions[i];
    if (prev?.id !== next?.id || prev?.lang !== next?.lang) {
      return false;
    }
  }
  return true;
}

type UsePlayerControllerOptions = {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  src: string;
  subtitles?: ReadonlyArray<Sub>;
  setIsPlaying: (b: boolean) => void;
  assetId?: string;
  titleId?: string;
  user?: UserType | null;
  authenticatedClient?: AuthenticatedClient;
};

export function usePlayerControllerSetup({
  videoRef,
  src,
  subtitles,
  setIsPlaying,
  assetId,
  titleId,
  user,
  authenticatedClient,
}: UsePlayerControllerOptions) {
  const controllerRef = React.useRef<PlayerController | null>(null);
  const loadedSourceRef = React.useRef<string | null>(null); // Track which source is currently loaded
  const [time, setTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [levels, setLevels] = React.useState<ReadonlyArray<Level>>([]);
  const [selectedLevel, setSelectedLevel] = React.useState<number | "auto">("auto");
  const [captions, setCaptions] = React.useState<ReadonlyArray<CaptionTrack>>([]);
  const [activeCaption, setActiveCaption] = React.useState<number | "off">("off");
  const [playbackRate, setPlaybackRate] = React.useState<number>(1);
  const { playerTelemetryHandler } = useTelemetry();

  // Effect 1: Create and configure controller once (independent of source)
  React.useEffect(() => {
    // Skip if controller already exists (Strict Mode remount protection)
    if (controllerRef.current) {
      console.log("♻️ Controller already initialized, skipping duplicate creation");
      return;
    }

    const controller: PlayerController = createPlayerController();
    controllerRef.current = controller;

    // Set up progress handlers only if assetId, authenticated user, and client are provided
    if (assetId && user && authenticatedClient) {
      controller.setProgressHandlers({
        saveProgress: async (assetId: string, positionSec: number, durationSec: number) => {
          try {
            await authenticatedClient.postProgress({ assetId, positionSec, durationSec });
          } catch (error) {
            console.warn("Failed to save progress (auth may have expired):", error);
          }
        },
        loadProgress: async (assetId: string) => {
          try {
            const result = await authenticatedClient.getProgress(assetId);
            return result;
          } catch (error) {
            console.warn("Failed to load progress (auth may have expired):", error);
            return null;
          }
        },
      });
      controller.setAssetId(assetId);
    }

    // Set up token refresh for signed URLs if titleId is provided
    if (titleId && user && authenticatedClient) {
      controller.setTitleId(titleId);
      controller.setTokenRefreshHandler(async (titleId: string) => {
        try {
          const freshData = await authenticatedClient.getTitleAssets(titleId);
          return {
            hlsUrl: freshData.hlsUrl,
            subtitles: freshData.subtitles,
          };
        } catch (error) {
          console.error("Failed to refresh token:", error);
          throw error;
        }
      });
    }

    // Set up telemetry tracking
    controller.setTelemetryHandler(playerTelemetryHandler);

    const v = videoRef.current;
    if (v) {
      controller.attach(v);
      controller.onTimeupdate((t: number, d: number) => {
        setTime(t);
        setDuration(d);
      });
      controller.onError((e: unknown) => console.error("Player error", e));
      controller.onLevelsUpdated((ls: ReadonlyArray<Level>, sel: number | "auto") => {
        setLevels(ls);
        setSelectedLevel(sel);
      });
      controller.onCaptionsUpdated((list: ReadonlyArray<CaptionTrack>) => {
        // Deduplicate: only update if captions actually changed
        setCaptions((prevCaptions) => (areCaptionListsEqual(prevCaptions, list) ? prevCaptions : list));
      });

      controller.onCaptionChange((active: number | "off") => {
        setActiveCaption(active);
      });
      controller.onPlaybackRateChange((rate: number) => setPlaybackRate(rate));
    }

    return () => {
      controller.destroy();
      controllerRef.current = null;
      loadedSourceRef.current = null; // Reset loaded source on cleanup
    };
  }, [videoRef, assetId, titleId, user, authenticatedClient, playerTelemetryHandler]);

  // Effect 2: Load source when src or subtitles change
  React.useEffect(() => {
    const controller = controllerRef.current;
    const v = videoRef.current;

    if (!controller || !v) {
      return;
    }

    // Skip if we've already loaded this exact source (Strict Mode protection)
    if (loadedSourceRef.current === src) {
      console.log("♻️ Source already loaded, skipping duplicate load");
      return;
    }

    const subs: Array<Sub> | undefined = subtitles?.map((s) => ({ lang: s.lang, url: s.url, forced: s.forced })) as
      | Array<Sub>
      | undefined;

    // Mark this source as loaded before async operation
    loadedSourceRef.current = src;

    controller
      .load({ hlsUrl: src, subtitles: subs })
      .then(() => {
        // try autoplay muted for convenience
        v.muted = true;
        // Captions are already set via onCaptionsUpdated callback during load
        return controller
          .play()
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      })
      .catch(() => {
        // ignore; error event should already be reported
        // Reset loaded source on error so retry is possible
        loadedSourceRef.current = null;
      });
  }, [src, subtitles, setIsPlaying]);

  // Track play/pause state
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [videoRef, setIsPlaying]);

  return {
    controllerRef,
    time,
    duration,
    levels,
    selectedLevel,
    setSelectedLevel,
    captions,
    activeCaption,
    setActiveCaption,
    playbackRate,
    setPlaybackRate,
  } as const;
}
